import React, { useState } from "react";
import Pagination from "../../shared/Pagination";
import Loading from "../../shared/Loading";
import {
  CircleCheck,
  Clock,
  Info,
  Loader,
  LucideUpload,
  Search,
  SortAsc,
  XCircle,
} from "lucide-react";
import { Button, Dropdown } from "antd";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { useMutation, useQuery } from "react-query";
import { useError } from "../../context/ErrorProvider";
import { getTransactions } from "../../sdk/transactions/transaction";
import {
  exportAllSelectedTransactions,
  exportAllTransactions,
} from "../../sdk/exports/export";
import { downloadExportFile } from "../../utils/export";
import TransactionFilter from "../../components/filter/TransactionFilter";
import SingleTransaction from "../../components/transaction/SingleTransaction";

const Transactions = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [fromDate, setFromDate] = useState("2023-11-23");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [type, setType] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const formatAmount = useFormatAmount();
  const formattedDate = useFormattedDate();
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(50);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const { showError } = useError();
  const [openTransactionFilter, setOpenTransactionFilter] = useState(false);
  const [openLoanTransaction, setOpenLoanTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState({});

  const closeTransactionFilter = () => {
    setOpenTransactionFilter(false);
  };

  const closeLoanTransaction = () => {
    setOpenLoanTransaction(false);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = transactions.map((txn) => txn.id);
      setSelectedTransactions(allIds);
    } else {
      setSelectedTransactions([]);
    }
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const items = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => exportTransactions({ format: "csv" }),
    },
    {
      key: "pdf",
      label: "Export as PDF",
      onClick: () => exportTransactions({ format: "pdf" }),
    },
  ];

  const selectedItems = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => exportSelectedTransactions({ format: "csv" }),
    },
    {
      key: "pdf",
      label: "Export as PDF",
      onClick: () => exportSelectedTransactions({ format: "pdf" }),
    },
  ];

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "successful":
        return (
          <CircleCheck className="text-green-600 bg-green-100 rounded-full p-1 w-6 h-6" />
        );
      case "pending":
        return (
          <Clock className="text-yellow-600 bg-yellow-100 rounded-full p-1 w-6 h-6" />
        );
      case "failed":
        return (
          <XCircle className="text-red-600 bg-red-100 rounded-full p-1 w-6 h-6" />
        );
      default:
        return (
          <Clock className="text-gray-600 bg-gray-100 rounded-full p-1 w-6 h-6" />
        );
    }
  };

  const {
    refetch: refetchTransaction,
    isRefetching: isRefetchingTransactions,
  } = useQuery({
    queryKey: [
      "transactions",
      page,
      limit,
      fromDate,
      toDate,
      search,
      status,
      method,
      type,
      minAmount,
      maxAmount,
    ],
    queryFn: async () => {
      const response = await getTransactions(
        page,
        limit,
        fromDate,
        toDate,
        search,
        status,
        method,
        type,
        minAmount,
        maxAmount,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setTransactions(data.data);
      setPage(data?.meta?.page);
      setTotalPages(data.meta?.totalPages);
      setLimit(data.meta?.limit);
      setTotalItems(data?.meta?.total);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleSelectTransaction = (txnId) => {
    setSelectedTransactions((prev) =>
      prev.includes(txnId)
        ? prev.filter((id) => id !== txnId)
        : [...prev, txnId],
    );
  };

  const { mutate: exportTransactions, isLoading: loadingExportTransactions } =
    useMutation({
      mutationKey: ["export all transactions"],
      mutationFn: ({ format }) =>
        exportAllTransactions(
          format,
          fromDate,
          toDate,
          search,
          status,
          method,
          type,
        ),
      onSuccess: (blob, variables) => {
        downloadExportFile(blob, `Loan transactions.${variables.format}`);
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const {
    mutate: exportSelectedTransactions,
    isLoading: loadingExportSelectedTransactions,
  } = useMutation({
    mutationKey: ["export all selected transactions"],
    mutationFn: ({ format }) =>
      exportAllSelectedTransactions(format, selectedTransactions),
    onSuccess: (blob, variables) => {
      downloadExportFile(blob, `Loans.${variables.format}`);
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const toggleLoanTransaction = (txn) => {
    setSelectedTransaction(txn);
    setOpenLoanTransaction(true);
  };

  return (
    <>
      {openTransactionFilter && (
        <TransactionFilter
          closeFilters={closeTransactionFilter}
          handleApplyFilter={closeTransactionFilter}
          status={status}
          setStatus={setStatus}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          type={type}
          setType={setType}
          method={method}
          setMethod={setMethod}
          minAmount={minAmount}
          setMinAmount={setMinAmount}
          maxAmount={maxAmount}
          setMaxAmount={setMaxAmount}
        />
      )}

      {openLoanTransaction && (
        <SingleTransaction
          closeLoanTransaction={closeLoanTransaction}
          transaction={selectedTransaction}
        />
      )}

      <div>
        <div className="h-[35px] my-[10px] w-full  flex items-center justify-between">
          <p className="text-[#000000] font-medium text-[20px]">Transactions</p>
        </div>

        <div>
          <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
            <button
              disabled={isRefetchingTransactions}
              onClick={refetchTransaction}
              className="h-[40px] w-[117px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]"
            >
              {isRefetchingTransactions ? <Loading /> : <Loader size={17} />}
              Update
            </button>
            <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
              {selectedTransactions.length} Selected
            </div>
            <div
              onClick={() => setOpenTransactionFilter(true)}
              className="h-[40px] cursor-pointer w-[127px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
            >
              <SortAsc color="#000000" />
              <span className="text-[#000000]">Filter</span>
            </div>
            <div className="flex items-center border border-gray-300 rounded-md px-3 h-[40px] w-[50%] bg-white">
              <Search className="text-gray-500 w-5 h-5 mr-2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search loans, name, loan code..."
                className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
              />
            </div>
            <p className="text-[#000000] truncate text-[15px] font-normal">
              {transactions?.length} Results
            </p>
            {selectedTransactions?.length === 0 ? (
              <Dropdown menu={{ items }} trigger={["click"]} placement="bottom">
                <Button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
                  {loadingExportTransactions ? (
                    <Loading />
                  ) : (
                    <LucideUpload size={17} />
                  )}
                  <span>Export</span>
                </Button>
              </Dropdown>
            ) : (
              <Dropdown
                menu={{ items: selectedItems }}
                trigger={["click"]}
                placement="bottom"
              >
                <Button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
                  {loadingExportSelectedTransactions ? (
                    <Loading />
                  ) : (
                    <LucideUpload size={17} />
                  )}
                  <span>Export</span>
                </Button>
              </Dropdown>
            )}
          </div>
          <div>
            <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
              <div className="w-[5%]">
                <input
                  type="checkbox"
                  checked={
                    transactions.length > 0 &&
                    selectedTransactions.length === transactions.length
                  }
                  onChange={handleSelectAll}
                />
              </div>
              <div className="w-[9%] text-[#000] truncate font-medium text-[14px]">
                ID
              </div>
              <div className="w-[10%] text-[#000] font-medium text-[14px]">
                LOAN ID
              </div>
              <div className="w-[18%] text-[#000] font-medium text-[14px]">
                BORROWER
              </div>
              <div className="w-[10%] truncate text-[#000] font-medium text-[14px]">
                PAYMENT METHOD
              </div>
              <div className="w-[12%] truncate text-[#000] font-medium text-[14px]">
                TRANSACTION TYPE
              </div>
              <div className="w-[13%] text-[#000] font-medium text-[14px]">
                AMOUNT
              </div>
              <div className="w-[13%] text-[#000] font-medium text-[14px]">
                DATE
              </div>
              <div className="w-[10%] text-[#000] font-medium text-[14px]">
                STATUS
              </div>
            </div>
            {transactions?.length > 0 ? (
              transactions.map((txn) => (
                <div
                  key={txn.id}
                  className="h-[40px] px-[10px] w-full bg-white mb-[7px] flex items-center"
                >
                  <div className="w-[5%]">
                    <input
                      type="checkbox"
                      checked={selectedTransactions.includes(txn.id)}
                      onChange={() => handleSelectTransaction(txn.id)}
                    />
                  </div>
                  <div
                    onClick={() => toggleLoanTransaction(txn)}
                    className="w-[9%] text-[#2A8C52] truncate cursor-pointer font-normal text-sm"
                  >
                    {txn?.public_id}
                  </div>
                  <div className="w-[10%] capitalize text-[#000] font-normal text-sm">
                    {txn?.loanPublicId}
                  </div>
                  <div className="w-[18%] capitalize text-[#000] font-normal text-sm">
                    {txn?.borrower?.name}
                  </div>
                  <div className="w-[10%] capitalize text-[#000] font-normal text-sm">
                    {txn?.paymentMethod}
                  </div>
                  <div className="w-[12%] truncate text-[#000] font-normal text-sm">
                    {txn?.transactionTypeLabel.length > 0
                      ? txn?.transactionTypeLabel
                      : txn?.transactionTypeLabel}
                  </div>
                  <div className="w-[13%] text-[#000] font-normal text-sm">
                    {formatAmount(txn?.amount ?? 0)}
                  </div>
                  <div className="w-[13%] text-[#000] font-normal text-sm">
                    {formattedDate(txn?.paymentDate)}
                  </div>
                  <div className="w-[10%] text-sm font-normal flex items-center gap-2">
                    {getStatusIcon(txn?.status)}
                    <span className="capitalize">{txn?.status}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="my-[20px] flex items-center gap-[10px]">
                <Info size={17} />
                <p>No loan transactions are available</p>
              </div>
            )}
          </div>
          <div className="h-[50px] mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[10px]">
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {start}–{end} of {totalItems}
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                Row/Page:
              </p>
              <p className="text-[15px] text-[#5C5C5C] font-normal">
                {page}/{totalPages}
              </p>
              <select
                className="h-[35px] w-[100px] px-[10px] text-[12px]"
                name="row"
                id="row"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              >
                <option value="10">10 items</option>
                <option value="20">20 items</option>
                <option value="25">25 items</option>
              </select>
            </div>
            <div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Transactions;
